import { useMemo, useState } from "react";
import { Check, X, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

const TASK_PRIORITIES = ["low", "medium", "high"];
const TASK_STATUSES = ["open", "in_progress", "done"];
const TASK_FILTERS = [
  { id: "active", label: "Active" },
  { id: "open", label: "Open" },
  { id: "in_progress", label: "In progress" },
  { id: "done", label: "Done" },
  { id: "all", label: "All" },
];

const TaskPriorityPill = ({ priority }) => {
  const map = {
    low: "border-[var(--ld-ink)]/25 text-[var(--ld-ink-muted)]",
    medium: "border-[var(--ld-gold-deep)]/60 text-[var(--ld-gold-deep)]",
    high: "border-red-700/40 text-red-700",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border ${
        map[priority] || map.medium
      }`}
    >
      {priority || "medium"}
    </span>
  );
};

const TaskRow = ({ row, onToggle, onUpdate, onDelete }) => {
  const isDone = row.status === "done";
  const overdue =
    !isDone && row.due_date && row.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div
      data-testid={`task-row-${row.id}`}
      className={`group flex items-start gap-4 px-5 py-4 border-b border-[var(--ld-border)]/70 last:border-b-0 transition-colors ${
        isDone ? "bg-[var(--ld-ink)]/[0.02]" : "hover:bg-[var(--ld-ink)]/[0.02]"
      }`}
    >
      <button
        data-testid={`task-toggle-${row.id}`}
        onClick={() => onToggle(row)}
        aria-label={isDone ? "Mark as open" : "Mark as done"}
        className={`mt-1 inline-flex h-5 w-5 items-center justify-center border transition-colors ${
          isDone
            ? "bg-[var(--ld-ink)] border-[var(--ld-ink)] text-[var(--ld-gold)]"
            : "border-[var(--ld-ink)]/40 hover:border-[var(--ld-gold-deep)]"
        }`}
      >
        {isDone && <Check size={13} strokeWidth={2} />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className={`text-[15px] text-[var(--ld-ink)] ${
              isDone ? "line-through opacity-60" : ""
            }`}
          >
            {row.title}
          </p>
          <TaskPriorityPill priority={row.priority} />
          {row.status === "in_progress" && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase border border-sky-700/40 text-sky-700">
              In progress
            </span>
          )}
        </div>
        {row.description && (
          <p
            className={`mt-1 text-[13px] text-[var(--ld-ink-muted)] leading-[1.65] ${
              isDone ? "line-through opacity-60" : ""
            }`}
          >
            {row.description}
          </p>
        )}
        <div className="mt-2 flex items-center gap-3 text-[11px] tracking-[0.12em] uppercase text-[var(--ld-ink-muted)]">
          {row.due_date ? (
            <span
              className={
                overdue
                  ? "text-red-700"
                  : "text-[var(--ld-ink-muted)]"
              }
            >
              Due · {new Date(row.due_date + "T00:00:00").toLocaleDateString()}
              {overdue && " · Overdue"}
            </span>
          ) : (
            <span>No due date</span>
          )}
          {row.completed_at && isDone && (
            <span>
              · Done {new Date(row.completed_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <select
          data-testid={`task-status-${row.id}`}
          value={row.status}
          onChange={(e) => onUpdate(row.id, { status: e.target.value })}
          className="bg-transparent border border-[var(--ld-ink)]/30 text-[11px] tracking-[0.12em] uppercase px-2 py-1.5 focus:outline-none focus:border-[var(--ld-gold-deep)]"
        >
          {TASK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </select>
        <select
          data-testid={`task-priority-${row.id}`}
          value={row.priority}
          onChange={(e) => onUpdate(row.id, { priority: e.target.value })}
          className="bg-transparent border border-[var(--ld-ink)]/30 text-[11px] tracking-[0.12em] uppercase px-2 py-1.5 focus:outline-none focus:border-[var(--ld-gold-deep)]"
        >
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <button
          data-testid={`task-delete-${row.id}`}
          onClick={() => {
            if (window.confirm("Delete this task?")) onDelete(row.id);
          }}
          className="text-[var(--ld-ink-muted)] hover:text-red-700 p-1 transition-colors"
          aria-label="Delete task"
        >
          <X size={15} strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
};

const TasksPanel = ({
  tasks,
  loading,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh,
}) => {
  const [filter, setFilter] = useState("active");
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [submitting, setSubmitting] = useState(false);

  const counts = useMemo(() => {
    const c = { all: tasks.length, open: 0, in_progress: 0, done: 0 };
    tasks.forEach((t) => {
      if (c[t.status] !== undefined) c[t.status] += 1;
    });
    c.active = c.open + c.in_progress;
    return c;
  }, [tasks]);

  const visible = useMemo(() => {
    if (filter === "all") return tasks;
    if (filter === "active")
      return tasks.filter((t) => t.status !== "done");
    return tasks.filter((t) => t.status === filter);
  }, [tasks, filter]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      toast("Add a title first", { duration: 2000 });
      return;
    }
    setSubmitting(true);
    try {
      await onCreate({
        title: trimmed,
        due_date: dueDate || null,
        priority,
      });
      setTitle("");
      setDueDate("");
      setPriority("medium");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = (row) =>
    onUpdate(row.id, { status: row.status === "done" ? "open" : "done" });

  return (
    <div data-testid="section-tasks">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Workspace
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Tasks
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
            Lightweight to-dos for the directory — follow-ups, approvals,
            internal notes. Add a task, set a due date, mark it done.
          </p>
        </div>
        <button
          data-testid="tasks-refresh"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      {/* Add task form */}
      <form
        data-testid="task-add-form"
        onSubmit={handleAdd}
        className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-3 border border-[var(--ld-border)]/80 p-4"
      >
        <input
          data-testid="task-input-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs doing?"
          className="md:col-span-6 h-11 bg-transparent border border-[var(--ld-ink)]/20 px-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
        <input
          data-testid="task-input-due"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="md:col-span-3 h-11 bg-transparent border border-[var(--ld-ink)]/20 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
        <select
          data-testid="task-input-priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="md:col-span-1 h-11 bg-transparent border border-[var(--ld-ink)]/20 px-3 text-sm text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        >
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <button
          data-testid="task-add-submit"
          type="submit"
          disabled={submitting}
          className="md:col-span-2 h-11 text-[12px] tracking-[0.18em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors disabled:opacity-60"
        >
          {submitting ? "Adding…" : "Add task"}
        </button>
      </form>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-2 text-[11px] tracking-[0.18em] uppercase">
        {TASK_FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`task-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 border transition-colors ${
                active
                  ? "bg-[var(--ld-ink)] text-[var(--ld-ivory)] border-[var(--ld-ink)]"
                  : "border-[var(--ld-ink)]/25 text-[var(--ld-ink-muted)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]"
              }`}
            >
              {f.label} · {counts[f.id] ?? 0}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="mt-6 border border-[var(--ld-border)]/80">
        {loading && (
          <div className="px-5 py-10 text-center text-[var(--ld-ink-muted)] text-sm">
            Loading tasks…
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="px-5 py-12 text-center text-[var(--ld-ink-muted)] text-sm">
            Nothing here. Add a task above to get started.
          </div>
        )}
        {!loading &&
          visible.map((row) => (
            <TaskRow
              key={row.id}
              row={row}
              onToggle={handleToggle}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
      </div>
    </div>
  );
};

export default TasksPanel;
